/**
 * azure-functions/shared/workflows/actionCatalogue.cjs
 *
 * The closed workflow action catalogue (WF-B-001, docs/workflow-engine-design.md
 * §3a). There is exactly one copy, shared by the browser and the server:
 * src/utils/workflowDefinition.js re-exports it, and the Function App requires
 * it — so the designer, the publish validator and the runner can never disagree
 * about which actions exist, what evidence each produces or how strong it is.
 *
 * Customers configure these actions; they never invent one, and a definition can
 * never raise an action's strength. Adding an entry here is a design change
 * (design §3a first, then here), never a tenant configuration.
 */

'use strict';

/** The ordinal assurance scale. `null` means "not applicable" (structural). */
const STRENGTHS = Object.freeze(['None', 'Low', 'Medium', 'Medium-High', 'High']);

const strengthRank = (s) => STRENGTHS.indexOf(s);

const CATEGORIES = Object.freeze(['Gather', 'Decide', 'Flow', 'Close']);

/**
 * Config field specs. `type` is one of: string, integer, number, stepId,
 * stepIds, fields, children, join, routes, enum. `required` fields must be
 * present; any field not listed is refused by the validator.
 */
const INSTRUCTIONS = { type: 'string', required: false };

const ACTIONS = [
  {
    id: 'collect-data',
    label: 'Collect data',
    category: 'Gather',
    kind: 'human',
    humanStep: true,
    strength: 'Low',
    evidence: 'The submitted form values',
    outcomes: { positive: [], negative: [] },
    configFields: { fields: { type: 'fields', required: true }, instructions: INSTRUCTIONS },
  },
  {
    id: 'fetch-data',
    label: 'Fetch data',
    category: 'Gather',
    kind: 'system',
    humanStep: false,
    strength: 'High',
    evidence:
      'An integration read, with source and fetch time — High only when fresh; a stale or disconnected read completes not-obtained with no evidence',
    outcomes: { positive: ['obtained'], negative: ['not-obtained'] },
    configFields: { source: { type: 'string', required: true } },
  },
  {
    id: 'upload-artifact',
    label: 'Upload artifact',
    category: 'Gather',
    kind: 'human',
    humanStep: true,
    strength: 'Medium',
    evidence: 'An artefact (register id and content hash)',
    outcomes: { positive: [], negative: [] },
    configFields: { artefactType: { type: 'string', required: false }, instructions: INSTRUCTIONS },
  },
  {
    id: 'attest',
    label: 'Attest',
    category: 'Gather',
    kind: 'human',
    humanStep: true,
    strength: 'Low',
    evidence: "A named person's statement",
    outcomes: { positive: [], negative: [] },
    configFields: { statement: { type: 'string', required: true }, instructions: INSTRUCTIONS },
  },
  {
    id: 'approve-reject',
    label: 'Approve / Reject',
    category: 'Decide',
    kind: 'human',
    humanStep: true,
    strength: 'Medium',
    evidence: 'A decision and reason by a named approver',
    outcomes: { positive: ['approve'], negative: ['reject'] },
    configFields: { reviews: { type: 'stepIds', required: true }, instructions: INSTRUCTIONS },
  },
  {
    id: 'review',
    label: 'Review',
    category: 'Decide',
    kind: 'human',
    humanStep: true,
    strength: 'Medium-High',
    evidence: "A reviewer's findings on named inputs",
    outcomes: { positive: ['no-adverse'], negative: ['adverse'] },
    configFields: { reviews: { type: 'stepIds', required: true }, instructions: INSTRUCTIONS },
  },
  {
    id: 'sample',
    label: 'Sample',
    category: 'Decide',
    kind: 'human',
    humanStep: true,
    strength: 'High',
    evidence: 'A selected sample and a per-item result',
    outcomes: { positive: ['pass'], negative: ['fail'] },
    configFields: {
      population: { type: 'string', required: true },
      percent: { type: 'number', required: false },
      count: { type: 'integer', required: false },
      instructions: INSTRUCTIONS,
    },
  },
  {
    id: 'effectiveness-check',
    label: 'Effectiveness check',
    category: 'Close',
    kind: 'human',
    humanStep: true,
    strength: 'High',
    evidence: 'An effective / not-effective / not-yet verdict and its resolving artefact',
    outcomes: { positive: ['effective'], negative: ['not-effective', 'not-yet'] },
    configFields: { criteria: { type: 'string', required: true }, instructions: INSTRUCTIONS },
  },
  {
    id: 'notify',
    label: 'Notify',
    category: 'Flow',
    kind: 'system',
    humanStep: false,
    strength: 'None',
    evidence: 'None — a dispatch is not evidence',
    outcomes: { positive: [], negative: [] },
    configFields: {
      recipientRole: { type: 'string', required: true },
      message: { type: 'string', required: false },
    },
  },
  {
    id: 'wait-timer',
    label: 'Wait / Timer',
    category: 'Flow',
    kind: 'system',
    humanStep: false,
    strength: 'None',
    evidence: 'None',
    outcomes: { positive: [], negative: [] },
    configFields: { durationDays: { type: 'integer', required: true } },
  },
  {
    id: 'branch',
    label: 'Branch',
    category: 'Flow',
    kind: 'structural',
    humanStep: false,
    strength: null,
    evidence: 'None',
    outcomes: { positive: [], negative: [] },
    configFields: {
      on: { type: 'stepId', required: true },
      field: { type: 'string', required: false },
      routes: { type: 'routes', required: true },
      otherwise: { type: 'stepId', required: false },
    },
  },
  {
    id: 'parallel-group',
    label: 'Parallel group',
    category: 'Flow',
    kind: 'structural',
    humanStep: false,
    strength: null,
    evidence: 'None of its own — rolls up its children',
    outcomes: { positive: [], negative: [] },
    configFields: {
      children: { type: 'children', required: true },
      join: { type: 'join', required: true },
    },
  },
  {
    id: 'raise-finding',
    label: 'Raise finding',
    category: 'Close',
    kind: 'human-handoff',
    humanStep: true,
    strength: null,
    evidence: 'The finding proposal and its triage decision',
    outcomes: { positive: ['raised', 'dismissed'], negative: [] },
    configFields: {
      target: { type: 'enum', values: ['findings-register'], required: false },
      summary: { type: 'string', required: false },
    },
  },
];

const deepFreeze = (o) => {
  Object.values(o).forEach((v) => {
    if (v && typeof v === 'object' && !Object.isFrozen(v)) deepFreeze(v);
  });
  return Object.freeze(o);
};

const ACTION_CATALOGUE = deepFreeze(ACTIONS);

const ACTION_BY_ID = Object.freeze(Object.fromEntries(ACTION_CATALOGUE.map((a) => [a.id, a])));

/**
 * The structural End marker (design §3a). Not an action and not in the
 * catalogue: it carries `config.outcome` ('pass' | 'fail') and produces no
 * evidence. It exists so a path's result is declared, never inferred.
 */
const END_MARKER = deepFreeze({
  id: 'end',
  label: 'End',
  kind: 'structural',
  humanStep: false,
  strength: null,
  configFields: { outcome: { type: 'enum', values: ['pass', 'fail'], required: true } },
});

/** Join rules a Parallel group may declare (design §3c). */
const JOIN_TYPES = Object.freeze(['all', 'any', 'n-of-m']);

/** Fixed run frequencies a `schedule` trigger may carry (design §3e). */
const SCHEDULE_FREQUENCIES = Object.freeze(['1m', '3m', '6m', '1y']);

/**
 * Subject types a `subject-date` trigger may anchor on. Ships EMPTY in v1
 * (design §3e): a subject type joins only in the PR that lands its migration
 * wave and retires the bespoke path, so no process is ever run by the engine
 * and a bespoke sweeper at the same time.
 */
const SUBJECT_DATE_REGISTRY = Object.freeze({});

function isNegativeOutcome(actionId, outcome) {
  const a = ACTION_BY_ID[actionId];
  return !!a && a.outcomes.negative.includes(outcome);
}

module.exports = {
  STRENGTHS,
  CATEGORIES,
  ACTION_CATALOGUE,
  ACTION_BY_ID,
  END_MARKER,
  JOIN_TYPES,
  SCHEDULE_FREQUENCIES,
  SUBJECT_DATE_REGISTRY,
  strengthRank,
  isNegativeOutcome,
};
